import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { acquireMicStream, primeMediaOnGesture } from "@/lib/audioRecorder";
import {
  cacheGateClipBlob,
  revokeGateClipBlobs,
  setGateAudioContext,
  stopAllAudio,
} from "@/lib/gateAudio";
import ConsentGate, { type Gate, type TerminateInfo } from "@/components/interview/ConsentGate";
import QuestionFlow from "@/components/interview/QuestionFlow";
import FinalConsentRecording from "@/components/interview/FinalConsentRecording";
import MediumChoice from "@/components/interview/MediumChoice";
import WrittenConsent from "@/components/interview/WrittenConsent";
import PublicityRelease from "@/components/interview/PublicityRelease";
import GeoGate from "@/components/interview/GeoGate";
import QuestionsCompleteGate from "@/components/interview/QuestionsCompleteGate";
import type { ConsentStatementsPayload } from "@/lib/interviewTypes";
import { INTERVIEW_QUESTIONS } from "@/lib/interviewContent";
import {
  readStoredSession,
  writeStoredSession,
  clearStoredSession,
  type StoredSession,
} from "@/lib/interviewSession";

const WELCOME_AUDIO_SRC = "/audio/interview/v1/entry.mp3";

const clipUrl = (id: string) => `/audio/interview/v1/${id}.mp3`;

// ---- Narration preload, PRIORITIZED + THROTTLED (Audio Residual A, Phase 3) --
// ROOT CAUSE, confirmed on-device: the old preload fired all 28 clip fetches at
// once and saturated the cellular link — every clip but the tiny intro took ~58-60s
// to land. The consent gate plays within SECONDS of Begin, so the gate blobs were
// never registered in time; playback fell back to the origin URL, which was starved
// in the very same 28-way jam. The robotic voice was our own preload competing with
// the clip it was supposed to be helping.
//
// So: fetch only what the first minute needs, two at a time, and do not start
// anything else until that group has settled. Bandwidth is the scarce resource here
// — issuing fewer requests is what makes the important ones arrive.
//
// PRIORITY: the three gate clips, registered as Blobs (Phase 2) so playback reads
// them from memory.
//
// entry.mp3 is deliberately NOT here. onBegin creates `new Audio(WELCOME_AUDIO_SRC)`
// a few lines below and plays it immediately, so the welcome element loads that file
// itself — preloading it would only duplicate that request while holding a priority
// slot ahead of the gate clips. Worse on the resume path, which returns BEFORE the
// welcome audio ever plays: entry would be fetched there for nothing.
const PRELOAD_PRIORITY_URLS: readonly string[] = [
  "gate-consent",
  "gate-state",
  "gate-dob",
].map(clipUrl);

// DEFERRED: everything else, in the order it is needed. Started only once the
// priority group has settled, and kept to a small pool so it can never again starve
// an in-flight gate clip or answer upload. HTTP-cache warm only — not Blobs.
const PRELOAD_DEFERRED_URLS: readonly string[] = [
  ...Array.from({ length: 23 }, (_, i) => `q${String(i + 1).padStart(2, "0")}`),
  "final",
].map(clipUrl);

// In-flight caps. Deliberately small: the entire bug was too many parallel fetches
// on a cellular link, so 2 is the conservative read of "a couple at a time".
const PRELOAD_PRIORITY_CONCURRENCY = 2;
const PRELOAD_DEFERRED_CONCURRENCY = 2;

const GATE_ORDER: Gate[] = ["consent", "state", "dob"];

// The three spoken-gate clips — held in memory as RETAINED Blobs (Audio Residual A,
// Phase 2): decoded once and cached across gate retries. They play right after a
// long network idle, which is exactly when a cold-cellular media element errors
// instantly. (The 23 question clips are ALSO registered as Blobs now — see
// QUESTION_CLIP_URLS below — but non-retained; `final` stays HTTP-warm-only.)
//
// COUPLING: these strings must match ConsentGate's GATE_CONFIG mp3Url values, which
// are the keys playPrompt looks up. GATE_CONFIG is not exported, so this is a
// duplicated literal. It fails SAFE — a drift here means the blob is simply never
// found and the clip loads from the network as it does today.
const GATE_CLIP_URLS: ReadonlySet<string> = new Set([
  "/audio/interview/v1/gate-consent.mp3",
  "/audio/interview/v1/gate-state.mp3",
  "/audio/interview/v1/gate-dob.mp3",
]);

// The 23 question prompt clips. Registered as Blobs too (C2 fix) so playPrompt
// routes them through the gesture-independent Web Audio path — the post-skip
// prompt's element play() is rejected by iOS (NotAllowedError) because uploadSkip's
// network round-trip elapses the tap's user-activation window. Registered with
// retainDecoded=false: unlike the few short gate clips, holding 23 decoded PCM
// buffers for the whole interview would cost tens of MB, so questions decode-on-play.
// `final` is deliberately NOT here — it stays HTTP-warm-only, unchanged.
//
// Derived from INTERVIEW_QUESTIONS (the real content source) via the same clipUrl
// template used everywhere below — NOT a hardcoded q01..q23 range — so the set can
// never silently drift out of sync with questionMp3()'s keys if a question is
// added, removed, or renumbered.
const QUESTION_CLIP_URLS: ReadonlySet<string> = new Set(
  INTERVIEW_QUESTIONS.map((q) => clipUrl(q.id)),
);
// Routed by interview-issue-magic-link as the reply-to/support address on the
// magic-link email a subject receives. Keep these consistent or both paths
// strand stuck subjects.
const SUPPORT_EMAIL = "support@legacylensai.com";

// Warm ONE clip. Gate clips are additionally kept as Blobs so playback never needs
// the network; everything else is drained purely to complete its HTTP cache entry.
//
// The body MUST be consumed either way: a bare fetch() leaves the response's
// ReadableStream unread, so the transfer can be cancelled and the cache entry left
// incomplete — the warm we think we are doing may not happen at all.
//
// Never throws. Best-effort by contract: a clip that fails to warm just plays from
// the network later, which is the pre-existing behaviour.
const warmClip = async (url: string): Promise<void> => {
  try {
    const r = await fetch(url, { cache: "force-cache" });
    // A 404/5xx still RESOLVES the fetch. Bail before caching, or an error page
    // would be registered as the clip's audio and the gate would try to play HTML.
    if (!r.ok) return;
    if (GATE_CLIP_URLS.has(url)) {
      cacheGateClipBlob(url, await r.blob());
      return;
    }
    if (QUESTION_CLIP_URLS.has(url)) {
      // Non-retained blob: decoded fresh on play, not held for the session.
      cacheGateClipBlob(url, await r.blob(), false);
      return;
    }
    await r.arrayBuffer();
  } catch {
    // Swallowed: nothing awaits this and it must never surface into the flow.
  }
};

// Run `urls` through warmClip with at most `limit` requests in flight, resolving
// once every one has settled. The point is the CAP, not the ordering: workers pull
// from a shared cursor, so a slow clip delays only its own worker.
//
// Cannot reject — warmClip swallows its own errors, so no task can throw.
const warmClips = async (urls: readonly string[], limit: number): Promise<void> => {
  let cursor = 0;
  const worker = async () => {
    while (cursor < urls.length) {
      const index = cursor++;
      await warmClip(urls[index]);
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, urls.length) }, worker),
  );
};

type Stage =
  | "loading"
  | "ready"
  | "playing"
  // Written, signed consent (counsel requirement): completes BEFORE the spoken
  // ConsentGate trio and therefore before any recording of any kind. Entered
  // after the welcome audio, exits to gate_geo on acceptance.
  | "written_consent"
  // Publicity / right-of-publicity release (counsel requirement): the SECOND signed
  // document, distinct from written consent. Sits right after written_consent and
  // before the geography gate — still before any recording. On acceptance,
  // PublicityRelease advances to gate_geo.
  | "publicity_release"
  // Pre-recording geography eligibility gate (two US-state dropdowns). Sits
  // between publicity_release and the spoken gates — before any recording. Standalone
  // (NOT part of GATE_ORDER); eligible -> gate_consent, ineligible -> gate_terminated.
  | "gate_geo"
  | "gate_consent"
  | "gate_state"
  | "gate_dob"
  | "gates_complete"
  | "assembling"
  // Photo hand-off: after consent is final, if the tribute is still awaiting
  // photos, the subject hands the phone to a helper to capture the photogrammetry
  // set (same-tab -> /upload/:slug) before returning to the questions. Shown ONLY
  // while tributes.status === "awaiting_photos"; once photos submit it never
  // re-appears (loop-safe).
  | "photo_handoff"
  // Post-upload handoff: the PRE-upload photo_handoff sends the subject OUT to
  // capture photos; this one is entered AFTER the photos are in (tribute flipped
  // to "processing") to bring them back IN to record the interview. Reached only
  // via a validated ?stage=post_photo_handoff resume — see handleResume.
  | "post_photo_handoff"
  | "consent_assembled"
  | "questions_complete"
  | "final_consent_recording"
  | "final_consent_recorded"
  | "question_mic_denied"
  | "exited"
  | "assemble_retry"
  | "assemble_failed"
  | "resume_retry"
  | "session_expired"
  | "gate_terminated"
  | "gate_max_retries"
  | "mic_denied"
  | "interview_error"
  | "token_invalid"
  | "token_expired"
  | "interview_complete"
  | "interview_terminated"
  | "missing_token"
  | "generic_error";

const GATE_STAGES: Record<Gate, Stage> = {
  consent: "gate_consent",
  state: "gate_state",
  dob: "gate_dob",
};

type ConsumeResponse = {
  ok: true;
  resume_token: string;
  interview_id: string;
  interview_status: string;
  tribute_status?: string | null;
  tribute_slug: string;
  session_expires_at: string;
  answered_question_ids: string[];
  skipped_question_ids: string[];
  consent_statements: ConsentStatementsPayload;
};

type SessionStatusResponse = {
  ok: true;
  interview_status: string;
  tribute_status?: string | null;
  answered_question_ids: string[];
  skipped_question_ids: string[];
  consent_statements: ConsentStatementsPayload;
  capture_medium: "video" | "audio" | null;
};

type ConsumeErrorBody = {
  error?: string;
  interview_status?: string;
};

type AssembleResponse = {
  ok: true;
  interview_id: string;
  consent_audio_url: string;
  status: "consent_complete";
  tribute_status?: string | null;
};

type AssembleErrorBody = {
  error?: string;
  interview_status?: string;
  missing_gates?: string[];
};

const TERMINAL_ROW_STATUSES = new Set([
  "terminated_ineligible",
  "terminated_declined",
  "complete",
]);

const COMPLETE_TRIBUTE_STATUSES = new Set([
  "interview_complete_pending_consent",
  "voice_training_queued",
  "voice_training_complete",
  "voice_training_failed",
]);

// interviews.status values that mean consent is already FINAL. On re-entry the
// consent gates — the ONLY path to the decline action — must be skipped. This
// drives both consent-integrity guard layers (onBegin primary + the gate-stage
// backstop effect). consent_pending is deliberately excluded: a still-pending
// interview runs the gates normally.
const CONSENT_FINAL_STATUSES = new Set([
  "consent_complete",
  "in_progress",
  "awaiting_final_consent",
]);

// Stages where the server has confirmed the interview is finished/terminal or the
// session token is dead, so the stored credential is useless and must not persist
// on the device (it now lives in localStorage too, which survives a closed tab).
// A single effect clears on entering any of these — see the DEAD_SESSION_STAGES
// effect below. Deliberately excludes transient/local stages (resume_retry,
// assemble_retry, generic_error, interview_error, assemble_failed, gate_terminated,
// mic_denied, question_mic_denied, gate_max_retries, exited) where the session may
// still be valid and clearing would strand a recoverable subject.
const DEAD_SESSION_STAGES = new Set<Stage>([
  "final_consent_recorded",
  "interview_terminated",
  "interview_complete",
  "session_expired",
]);

// Where the flow goes once consent is FINAL: the photo hand-off if the tribute is
// still awaiting photos, otherwise straight to the question flow. The single
// source of this branch, used at every consent-final entry (fresh assemble +
// both resume paths). LOOP-SAFE: photos submitting flips awaiting_photos ->
// processing, so re-entry falls through to consent_assembled; nothing re-shows
// the hand-off once tributes.status !== "awaiting_photos".
const stageAfterConsentFinal = (
  tributeStatus: string | null | undefined,
): Stage => (tributeStatus === "awaiting_photos" ? "photo_handoff" : "consent_assembled");

const stripUrlParams = (...names: string[]) => {
  const url = new URL(window.location.href);
  if (!names.some((name) => url.searchParams.has(name))) return;
  names.forEach((name) => url.searchParams.delete(name));
  window.history.replaceState({}, "", url.pathname + (url.search ? url.search : "") + url.hash);
};

const stageFromConsumeError = (status: number | undefined, body: ConsumeErrorBody | null): Stage => {
  if (status === 404) return "token_invalid";
  if (status === 410) return "token_expired";
  if (status === 409) {
    const rowStatus = body?.interview_status ?? "";
    if (TERMINAL_ROW_STATUSES.has(rowStatus)) return "interview_terminated";
    if (COMPLETE_TRIBUTE_STATUSES.has(rowStatus)) return "interview_complete";
    // Unrecognized or empty 409 status (e.g. a 409 whose body failed to parse,
    // leaving rowStatus ""): a 409 on re-entry means consume-token REFUSED entry
    // because the interview is DONE, not because something broke. Default to the
    // gentle "you've already completed this interview" page — never the alarming
    // "this interview can't be resumed / contact us" terminated page. Genuine
    // terminations are the explicit TERMINAL_ROW_STATUSES case above.
    return "interview_complete";
  }
  return "generic_error";
};

const InterviewLanding = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [stage, setStage] = useState<Stage>("loading");
  const [terminateInfo, setTerminateInfo] = useState<TerminateInfo | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Setup-time recording-medium gate (v1 simplification step 3).
  //   undefined = not yet fetched for this consent_assembled entry
  //   null      = fetched, not chosen -> render MediumChoice
  //   "video"|"audio" = chosen -> render QuestionFlow
  const [captureMedium, setCaptureMedium] =
    useState<"video" | "audio" | null | undefined>(undefined);
  const [captureMediumError, setCaptureMediumError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  // Persistent mic stream for the SPOKEN GATE TRIO only (consent/state/dob).
  // Acquired when the flow enters gate_consent (AFTER written-consent/publicity
  // signing — the mic stays off during signing) and reused across the three gates
  // so iOS Safari does not re-prompt per gate. Released when the trio ends (never
  // leaked into QuestionFlow, which keeps acquiring its own fresh stream) and on
  // unmount.
  const micStreamRef = useRef<MediaStream | null>(null);
  // Guards the one-time clip preload (see onBegin) so a double-tap can't re-fire it.
  const preloadStartedRef = useRef(false);
  const consumedRef = useRef(false);

  const releaseGateMicStream = useCallback(() => {
    const s = micStreamRef.current;
    if (s) {
      s.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
  }, []);

  const storedSession = slug ? readStoredSession(slug) : null;

  // Single enforcement point: erase the stored credential (both stores) the
  // moment the flow enters a stage where the server has confirmed the session is
  // dead. Runs after commit, so it never disturbs the render that entered the
  // stage — and every DEAD_SESSION_STAGES render is static, reading no session.
  useEffect(() => {
    if (slug && DEAD_SESSION_STAGES.has(stage)) clearStoredSession(slug);
  }, [stage, slug]);

  const handleConsume = useCallback(
    async (token: string, slugForKey: string) => {
      try {
        const { data, error } = await supabase.functions.invoke<ConsumeResponse>(
          "interview-consume-token",
          { body: { token } },
        );

        // supabase-js wraps non-2xx into a FunctionsHttpError with the response
        // attached on .context.
        if (error) {
          const context = (error as unknown as { context?: Response }).context;
          let status: number | undefined;
          let body: ConsumeErrorBody | null = null;
          if (context && typeof context.status === "number") {
            status = context.status;
            try {
              body = (await context.clone().json()) as ConsumeErrorBody;
            } catch {
              body = null;
            }
          }
          setStage(stageFromConsumeError(status, body));
          return;
        }

        if (!data?.ok) {
          setStage("generic_error");
          return;
        }

        const stored: StoredSession = {
          resume_token: data.resume_token,
          interview_id: data.interview_id,
          tribute_slug: data.tribute_slug,
          session_expires_at: data.session_expires_at,
          interview_status: data.interview_status,
          tribute_status: data.tribute_status ?? null,
          answered_question_ids: data.answered_question_ids ?? [],
          skipped_question_ids: data.skipped_question_ids ?? [],
          consent_statements: data.consent_statements,
        };
        writeStoredSession(slugForKey, stored);
        stripUrlParams("token");
        setStage("ready");
      } catch (err) {
        console.error("interview-consume-token network failure", err);
        setStage("generic_error");
      }
    },
    [],
  );

  // Tokenless reload (same-tab refresh): the magic-link token was stripped after
  // first consume, so we re-validate the LIVE interview status via the stored
  // session token before routing — never trusting the stale consume-time snapshot
  // (which could route a consented subject back through the gates / decline path).
  const handleResume = useCallback(
    async (slugForKey: string, stored: StoredSession, requestedStage?: string | null) => {
      setStage("loading");
      try {
        const { data, error } = await supabase.functions.invoke<SessionStatusResponse>(
          "interview-session-status",
          { headers: { Authorization: `Bearer ${stored.resume_token}` } },
        );
        if (error) {
          const context = (error as unknown as { context?: Response }).context;
          const status =
            context && typeof context.status === "number" ? context.status : undefined;
          // 401/410 — session token dead/expired. Re-auth via the (non-burning)
          // email link; NOT a gate, NOT a silent resume.
          if (status === 401 || status === 410) {
            setStage("session_expired");
            return;
          }
          // Transient (500/502/network). NEVER fall through to ready/gates on
          // uncertainty — show a retry card that re-fires this revalidation.
          setStage("resume_retry");
          return;
        }
        if (!data?.ok) {
          setStage("resume_retry");
          return;
        }
        // Terminal interview — re-entry is blocked (mirrors interview-consume-
        // token's 409 handling). Route to the terminal card, NEVER to the gates,
        // so gate-routing is reachable IFF the live status is consent_pending.
        if (TERMINAL_ROW_STATUSES.has(data.interview_status)) {
          setStage("interview_terminated");
          return;
        }
        // Refresh the stored snapshot with the LIVE status + progress, then enter.
        const refreshed: StoredSession = {
          ...stored,
          interview_status: data.interview_status,
          tribute_status: data.tribute_status ?? null,
          answered_question_ids: data.answered_question_ids ?? [],
          skipped_question_ids: data.skipped_question_ids ?? [],
          consent_statements: data.consent_statements,
        };
        writeStoredSession(slugForKey, refreshed);
        // Honor the post-upload handoff request ONLY after the server-validated
        // resume succeeded AND the live interview status is consent-final — never
        // trusting the URL param on its own. Strip it so a refresh / back-button /
        // bookmark doesn't re-force the handoff screen. Every other path (401/410,
        // transient, terminal above) is unaffected.
        if (
          requestedStage === "post_photo_handoff" &&
          CONSENT_FINAL_STATUSES.has(refreshed.interview_status)
        ) {
          stripUrlParams("stage");
          setStage("post_photo_handoff");
        } else {
          setStage("ready");
        }
      } catch (err) {
        console.error("interview-session-status network failure", err);
        setStage("resume_retry");
      }
    },
    [],
  );

  useEffect(() => {
    if (!slug) {
      setStage("missing_token");
      return;
    }
    if (consumedRef.current) return;
    consumedRef.current = true;

    const token = searchParams.get("token");
    if (token) {
      void handleConsume(token, slug);
      return;
    }

    const stored = readStoredSession(slug);
    if (stored) {
      const requestedStage = searchParams.get("stage");
      void handleResume(slug, stored, requestedStage);
      return;
    }

    setStage("missing_token");
  }, [slug, searchParams, handleConsume, handleResume]);

  const onBegin = useCallback(() => {
    // Stop any in-flight audio before starting — closes the refresh→Begin overlap
    // (a prior welcome audio, or a gate/question prompt / stale SpeechSynthesis).
    audioRef.current?.pause();
    stopAllAudio();

    // Prime AudioContext + SpeechSynthesis inside the user gesture so the
    // subsequent auto-advance (into gate 1, or straight into the question flow on
    // resume) is allowed on iOS Safari.
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = primeMediaOnGesture();
      } catch (err) {
        console.error("primeMediaOnGesture failed", err);
      }
    }
    // Hand the gesture-unlocked context to gateAudio so the gate clips can play
    // through Web Audio (the autoplay fix). This is the ONLY context the app
    // creates, and it is created inside this tap — which is precisely what makes
    // gesture-less AudioBufferSourceNode playback legal later, where
    // HTMLAudioElement.play() is not.
    //
    // Set on EVERY Begin, before the resume-skip return below, so the value can
    // never be stale relative to audioContextRef.
    setGateAudioContext(audioContextRef.current);

    // Warm the narration clips — PRIORITIZED and THROTTLED (Phase 3). The three gate
    // clips go first, two at a time; the other 24 do not get a single byte until they
    // have settled. Firing all 28 at once is what starved the gate clips to ~60s and
    // produced the robotic voice.
    //
    // Runs ONCE per mount; never awaited, so Begin stays instant and the welcome
    // audio starts immediately. Every error is swallowed inside warmClip, so this
    // can neither block nor throw into the flow. Placed BEFORE the resume-skip
    // return so the question clips warm on the resume path too.
    if (!preloadStartedRef.current) {
      preloadStartedRef.current = true;
      void (async () => {
        await warmClips(PRELOAD_PRIORITY_URLS, PRELOAD_PRIORITY_CONCURRENCY);
        await warmClips(PRELOAD_DEFERRED_URLS, PRELOAD_DEFERRED_CONCURRENCY);
      })();
    }

    // PRIMARY consent-integrity guard: on re-entry past consent_pending, consent
    // is final — skip welcome + gates + assembler and go straight to the question
    // flow. The gate stages (the only path to the decline action) are never
    // entered, and the assembler does NOT re-fire (it runs only from
    // gates_complete, which resume bypasses).
    const resume = slug ? readStoredSession(slug) : null;
    if (resume && CONSENT_FINAL_STATUSES.has(resume.interview_status)) {
      setStage(stageAfterConsentFinal(resume.tribute_status));
      return;
    }

    // Welcome audio -> written_consent (NOT straight to gate_consent). Written,
    // signed consent must complete before any recording, including the spoken
    // gate recordings. On acceptance, WrittenConsent advances to gate_consent.
    setStage("playing");
    const audio = new Audio(WELCOME_AUDIO_SRC);
    audioRef.current = audio;
    audio.addEventListener("ended", () => setStage("written_consent"));
    audio.addEventListener("error", () => {
      console.error("welcome audio playback failed");
      setStage("written_consent");
    });
    void audio.play().catch((err) => {
      console.error("welcome audio play() rejected", err);
      setStage("written_consent");
    });
  }, [slug]);

  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.src = "";
        audioRef.current = null;
      }
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== "closed") {
        void ctx.close().catch(() => undefined);
        audioContextRef.current = null;
      }
      // Drop gateAudio's reference to the context we just closed. Without this it
      // holds a closed context and every gate clip takes the "context_closed" miss
      // instead of the Web Audio path. Unconditional: it must be cleared even if the
      // context was already closed above.
      setGateAudioContext(null);
      releaseGateMicStream();
      // Release the gate-clip object URLs (Audio Residual A, Phase 2). Without this
      // the ~600KB of blobs stays pinned for the document's lifetime. Safe here:
      // this teardown is mount-scoped, so the gates are long finished, and a late
      // lookup would just miss and fall back to the network URL.
      revokeGateClipBlobs();
    };
  }, [releaseGateMicStream]);

  // Acquire the gate-phase mic stream when the flow ENTERS the spoken gate trio
  // (stage becomes gate_consent), NOT on the Begin gesture — so the mic stays off
  // during written-consent / publicity signing and only goes live once consent is
  // signed and the spoken gates begin. The consent gate plays its prompt before
  // recording, which covers the acquisition latency, so the stream is ready by the
  // first record. On denial/failure micStreamRef stays null and ConsentGate falls
  // back to acquiring per-gate. The cancellation guard stops a late-resolving
  // acquire from leaking if the subject leaves the gate before it lands.
  useEffect(() => {
    if (stage !== "gate_consent" || micStreamRef.current) return;
    let cancelled = false;
    acquireMicStream({
      noiseSuppression: true,
      echoCancellation: true,
      autoGainControl: true,
    })
      .then((s) => {
        if (cancelled) {
          s.getTracks().forEach((t) => t.stop());
          return;
        }
        micStreamRef.current = s;
      })
      .catch((err) => {
        console.error("gate mic stream acquire failed", err);
        micStreamRef.current = null;
      });
    return () => {
      cancelled = true;
    };
  }, [stage]);

  // Release the gate-phase mic stream whenever we are NOT in the spoken gate trio,
  // so it is held ONLY across consent/state/dob and never leaks into QuestionFlow
  // (which acquires its own stream) or lingers after a terminate/max-retries/error
  // exit. Releasing when micStreamRef is already null is a no-op, so this is safe
  // on every render (including the pre-gate stages, where no stream exists yet).
  useEffect(() => {
    const isSpokenGate =
      stage === "gate_consent" || stage === "gate_state" || stage === "gate_dob";
    if (!isSpokenGate) {
      releaseGateMicStream();
    }
  }, [stage, releaseGateMicStream]);

  const advanceFromGate = useCallback((current: Gate) => {
    const idx = GATE_ORDER.indexOf(current);
    const next = GATE_ORDER[idx + 1];
    if (next) {
      setStage(GATE_STAGES[next]);
    } else {
      setStage("gates_complete");
    }
  }, []);

  const handleGateTerminate = useCallback((info: TerminateInfo) => {
    setTerminateInfo(info);
    setStage("gate_terminated");
  }, []);

  const [maxRetriesGate, setMaxRetriesGate] = useState<Gate | null>(null);
  const handleGateMaxRetries = useCallback((gate: Gate) => {
    setMaxRetriesGate(gate);
    setStage("gate_max_retries");
  }, []);
  const [micDeniedGate, setMicDeniedGate] = useState<Gate | null>(null);
  const handleGateMicDenied = useCallback((gate: Gate) => {
    setMicDeniedGate(gate);
    setStage("mic_denied");
  }, []);
  const handleGateError = useCallback((message: string) => {
    setErrorMessage(message);
    setStage("interview_error");
  }, []);

  const handleQuestionsComplete = useCallback(() => {
    setStage("questions_complete");
  }, []);
  const handleQuestionsMicDenied = useCallback(() => {
    setStage("question_mic_denied");
  }, []);
  const handleQuestionsExit = useCallback(() => {
    setStage("exited");
  }, []);
  const handleFinalConsentComplete = useCallback(() => {
    setStage("final_consent_recorded");
  }, []);
  const handleStartFinalConsent = useCallback(() => {
    setStage("final_consent_recording");
  }, []);

  const retryCurrentGate = useCallback((gate: Gate) => {
    setErrorMessage(null);
    setStage(GATE_STAGES[gate]);
  }, []);

  // Re-fires the tokenless resume revalidation from the resume_retry card. Reads
  // slug + stored session fresh so it never routes off stale state.
  const retryResume = useCallback(() => {
    const stored = slug ? readStoredSession(slug) : null;
    if (!slug || !stored) {
      setStage("missing_token");
      return;
    }
    void handleResume(slug, stored);
  }, [slug, handleResume]);

  const [missingGates, setMissingGates] = useState<string[]>([]);
  const assembleStartedRef = useRef(false);

  const routeAssembleError = useCallback(
    (status: number | undefined, body: AssembleErrorBody | null) => {
      // 401/410 — session token gone/expired mid-flow. Re-auth class: reopen the
      // (non-burning) magic link. NOT retry, NOT support.
      if (status === 401 || status === 410) {
        setStage("session_expired");
        return;
      }
      // 409 — tribute past recording phase, or interviews row not in
      // {consent_pending, consent_complete}. Reuse the interview-status
      // classifier; explicit interview_error fallback (no silent default).
      if (status === 409) {
        const rowStatus = body?.interview_status ?? "";
        if (TERMINAL_ROW_STATUSES.has(rowStatus)) {
          setStage("interview_terminated");
          return;
        }
        if (COMPLETE_TRIBUTE_STATUSES.has(rowStatus)) {
          setStage("interview_complete");
          return;
        }
        setErrorMessage(body?.error ?? "This interview can't continue right now.");
        setStage("interview_error");
        return;
      }
      // 422 — a gate recording didn't persist. Terminal: the subject can't fix
      // this by re-recording. Support-only, no retry.
      if (status === 422) {
        setMissingGates(body?.missing_gates ?? []);
        setStage("assemble_failed");
        return;
      }
      // 500/502 and transport errors (status undefined): transient. Safe to
      // re-fire — the assembler manifest write is a deterministic overwrite
      // ordered before the status flip, with no consent_complete early-return.
      if (status === undefined || status === 500 || status === 502) {
        setStage("assemble_retry");
        return;
      }
      // Any other unexpected status: generic error, no misleading retry.
      setErrorMessage(body?.error ?? "Something went wrong finishing your consent.");
      setStage("interview_error");
    },
    [],
  );

  const runAssemble = useCallback(async () => {
    const stored = slug ? readStoredSession(slug) : null;
    if (!stored) {
      setStage("missing_token");
      return;
    }
    setStage("assembling");
    try {
      const { data, error } = await supabase.functions.invoke<AssembleResponse>(
        "interview-assemble-pre-consent",
        { headers: { Authorization: `Bearer ${stored.resume_token}` } },
      );
      if (error) {
        const ctx = (error as unknown as { context?: Response }).context;
        let status: number | undefined;
        let body: AssembleErrorBody | null = null;
        if (ctx && typeof ctx.status === "number") {
          status = ctx.status;
          try {
            body = (await ctx.clone().json()) as AssembleErrorBody;
          } catch {
            body = null;
          }
        }
        routeAssembleError(status, body);
        return;
      }
      if (!data?.ok) {
        // Malformed 2xx — treat as transient; re-firing is safe.
        setStage("assemble_retry");
        return;
      }
      // The ONLY transition past consent-assembly, and terminal-on-success (set +
      // return — nothing below runs on a clean 200). Branch on the assembler's
      // fresh tribute_status: photo hand-off if the tribute just flipped to
      // awaiting_photos, else consent_assembled (the question flow mounts solely
      // from that stage, so a 200 is structurally required before any answer).
      setStage(stageAfterConsentFinal(data.tribute_status));
      return;
    } catch (err) {
      // Network/transport throw (offline, DNS, CORS, reset) — the
      // status-undefined transient case, landing in a real catch.
      console.error("interview-assemble-pre-consent network failure", err);
      setStage("assemble_retry");
    }
  }, [slug, routeAssembleError]);

  // Fire the assembler POST exactly once on arrival at gates_complete (pre-POST).
  // The ref guard survives runAssemble identity changes and StrictMode double-
  // invoke; the Try-again button on assemble_retry re-fires via runAssemble
  // directly, bypassing this guard.
  useEffect(() => {
    if (stage === "gates_complete" && !assembleStartedRef.current) {
      assembleStartedRef.current = true;
      void runAssemble();
    }
  }, [stage, runAssemble]);

  // If we ever land in a gate stage without a stored session (cleared cookies,
  // expired session, etc.), bounce to the missing-token card. CONSENT-INTEGRITY
  // BACKSTOP: if a gate stage is somehow active while consent is already final,
  // redirect to the question flow — the decline path lives only in the gates, so
  // it must be structurally unreachable on re-entry past consent_pending. Done as
  // an effect so we never call setState during render.
  useEffect(() => {
    const isGateStage =
      stage === "gate_consent" || stage === "gate_state" || stage === "gate_dob";
    if (!isGateStage) return;
    if (!storedSession) {
      setStage("missing_token");
      return;
    }
    if (CONSENT_FINAL_STATUSES.has(storedSession.interview_status)) {
      setStage(stageAfterConsentFinal(storedSession.tribute_status));
    }
  }, [stage, storedSession]);

  // On entering consent_assembled (any path — fresh post-assembler, tokenless
  // reload, or magic-link re-entry), read the interview's CURRENT capture_medium
  // via the existing session-status endpoint (extended to return it). Single
  // source of truth for the MediumChoice-vs-QuestionFlow branch. Keyed on
  // resume_token (a stable string) — NOT the storedSession object, which is
  // re-read each render — so it fires exactly once per entry.
  useEffect(() => {
    if (stage !== "consent_assembled") return;
    if (captureMedium !== undefined || captureMediumError) return;
    if (!storedSession) return;
    let alive = true;
    (async () => {
      const { data, error } = await supabase.functions.invoke<SessionStatusResponse>(
        "interview-session-status",
        { headers: { Authorization: `Bearer ${storedSession.resume_token}` } },
      );
      if (!alive) return;
      if (error) {
        const context = (error as unknown as { context?: Response }).context;
        const status =
          context && typeof context.status === "number" ? context.status : undefined;
        if (status === 401 || status === 410) {
          setStage("session_expired");
          return;
        }
        setCaptureMediumError(true);
        return;
      }
      setCaptureMedium(data?.capture_medium ?? null);
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, captureMedium, captureMediumError, storedSession?.resume_token]);

  const renderQuestions = () => {
    if (!storedSession || !audioContextRef.current) return null;
    return (
      <QuestionFlow
        resumeToken={storedSession.resume_token}
        audioContext={audioContextRef.current}
        captureMedium={captureMedium}
        answeredQuestionIds={storedSession.answered_question_ids}
        skippedQuestionIds={storedSession.skipped_question_ids}
        onComplete={handleQuestionsComplete}
        onError={handleGateError}
        onMicDenied={handleQuestionsMicDenied}
        onExit={handleQuestionsExit}
      />
    );
  };

  const renderConsentAssembled = () => {
    if (!storedSession) return null;
    if (captureMediumError) {
      return (
        <div className="text-center">
          <p className="text-base text-muted-foreground">
            We could not load your setup. Please try again.
          </p>
          <Button
            size="lg"
            variant="outline"
            className="mt-4"
            onClick={() => setCaptureMediumError(false)}
          >
            Try again
          </Button>
        </div>
      );
    }
    if (captureMedium === undefined) {
      return (
        <div className="text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
        </div>
      );
    }
    if (captureMedium === null) {
      return (
        <MediumChoice
          sessionToken={storedSession.resume_token}
          currentMedium={null}
          onChosen={(m) => setCaptureMedium(m)}
        />
      );
    }
    return renderQuestions();
  };

  const renderFinalConsent = () => {
    if (!storedSession || !audioContextRef.current || !storedSession.consent_statements) return null;
    return (
      <FinalConsentRecording
        audioContext={audioContextRef.current}
        consentStatements={storedSession.consent_statements}
        sessionToken={storedSession.resume_token}
        onComplete={handleFinalConsentComplete}
        onError={handleGateError}
        onMicDenied={handleQuestionsMicDenied}
        onExit={handleQuestionsExit}
      />
    );
  };

  const renderWrittenConsent = () => {
    if (!storedSession) return null;
    return (
      <WrittenConsent
        resumeToken={storedSession.resume_token}
        writtenStatement={storedSession.consent_statements?.written ?? null}
        onAccepted={() => setStage("publicity_release")}
        onError={handleGateError}
        onSessionExpired={() => setStage("session_expired")}
      />
    );
  };

  // Publicity / right-of-publicity release — the SECOND signed document. Sits
  // between written_consent and gate_geo; on acceptance, advances to the geography
  // gate. Modeled on renderWrittenConsent.
  const renderPublicityRelease = () => {
    if (!storedSession) return null;
    return (
      <PublicityRelease
        resumeToken={storedSession.resume_token}
        publicityStatement={storedSession.consent_statements?.publicityRelease ?? null}
        onAccepted={() => setStage("gate_geo")}
        onError={handleGateError}
        onSessionExpired={() => setStage("session_expired")}
      />
    );
  };

  // Pre-recording geography gate (state-gate build). Standalone (NOT in
  // GATE_ORDER). Eligible -> into the spoken trio (gate_consent). Ineligible ->
  // reuse the existing geography-terminate path (handleGateTerminate ->
  // gate_terminated, ineligible_geography copy).
  const renderGeoGate = () => {
    if (!storedSession) return null;
    return (
      <GeoGate
        resumeToken={storedSession.resume_token}
        onAccepted={() => setStage("gate_consent")}
        onIneligible={() =>
          handleGateTerminate({
            status: "terminated_ineligible",
            reason: "ineligible_geography",
          })
        }
        onError={handleGateError}
        onSessionExpired={() => setStage("session_expired")}
      />
    );
  };

  // End-of-interview reconciliation gate (Finding #4 BUILD 2). Notify-only:
  // classifies the current answers' transcript status and shows one of five calm
  // states; the clean path continues via handleStartFinalConsent unchanged.
  const renderQuestionsComplete = () => {
    if (!storedSession) return null;
    return (
      <QuestionsCompleteGate
        resumeToken={storedSession.resume_token}
        onContinue={handleStartFinalConsent}
      />
    );
  };

  const renderGate = (gate: Gate) => {
    if (!storedSession || !audioContextRef.current) return null;
    return (
      <ConsentGate
        key={gate}
        gate={gate}
        resumeToken={storedSession.resume_token}
        audioContext={audioContextRef.current}
        micStream={micStreamRef.current ?? undefined}
        onAccept={() => advanceFromGate(gate)}
        onTerminate={handleGateTerminate}
        onMaxRetries={handleGateMaxRetries}
        onMicDenied={handleGateMicDenied}
        onError={handleGateError}
      />
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="px-4 py-4 flex justify-center">
        <Link to="/" className="opacity-70 hover:opacity-100 transition-opacity">
          <img src="/ll-mark-256.png" alt="LegacyLens AI" className="h-24" />
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="max-w-xl w-full">
          <StageContent
            stage={stage}
            onBegin={onBegin}
            renderGate={renderGate}
            renderWrittenConsent={renderWrittenConsent}
            renderPublicityRelease={renderPublicityRelease}
            renderGeoGate={renderGeoGate}
            renderQuestions={renderQuestions}
            renderConsentAssembled={renderConsentAssembled}
            renderFinalConsent={renderFinalConsent}
            renderQuestionsComplete={renderQuestionsComplete}
            terminateInfo={terminateInfo}
            errorMessage={errorMessage}
            maxRetriesGate={maxRetriesGate}
            micDeniedGate={micDeniedGate}
            onRetryGate={retryCurrentGate}
            onRetryAssemble={runAssemble}
            onRetryResume={retryResume}
            onGoToPhotoUpload={() => {
              // Same-tab navigation — sessionStorage (the resume token) MUST
              // survive so /upload can authorize the presign. No new tab / reload.
              if (slug) navigate(`/upload/${slug}`);
            }}
            missingGates={missingGates}
            interviewId={storedSession?.interview_id ?? null}
          />
        </div>
      </div>

      <footer className="py-6 text-center text-sm text-muted-foreground">
        <Link
          to="/digital-legacy-directive"
          className="underline underline-offset-4 hover:text-foreground transition-colors"
        >
          How we protect your rights →
        </Link>
      </footer>
    </div>
  );
};

type StageContentProps = {
  stage: Stage;
  onBegin: () => void;
  renderGate: (gate: Gate) => JSX.Element | null;
  renderWrittenConsent: () => JSX.Element | null;
  renderPublicityRelease: () => JSX.Element | null;
  renderGeoGate: () => JSX.Element | null;
  renderQuestions: () => JSX.Element | null;
  renderConsentAssembled: () => JSX.Element | null;
  renderFinalConsent: () => JSX.Element | null;
  renderQuestionsComplete: () => JSX.Element | null;
  terminateInfo: TerminateInfo | null;
  errorMessage: string | null;
  maxRetriesGate: Gate | null;
  micDeniedGate: Gate | null;
  onRetryGate: (gate: Gate) => void;
  onRetryAssemble: () => void;
  onRetryResume: () => void;
  onGoToPhotoUpload: () => void;
  missingGates: string[];
  interviewId: string | null;
};

const StageContent = ({
  stage,
  onBegin,
  renderGate,
  renderWrittenConsent,
  renderPublicityRelease,
  renderGeoGate,
  renderQuestions,
  renderConsentAssembled,
  renderFinalConsent,
  renderQuestionsComplete,
  terminateInfo,
  errorMessage,
  maxRetriesGate,
  micDeniedGate,
  onRetryGate,
  onRetryAssemble,
  onRetryResume,
  onGoToPhotoUpload,
  missingGates,
  interviewId,
}: StageContentProps) => {
  switch (stage) {
    case "loading":
      return (
        <div className="text-center" role="status" aria-live="polite">
          <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-lg text-muted-foreground">Verifying your link…</p>
        </div>
      );

    case "ready":
      return (
        <div className="text-center">
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Welcome
          </h1>
          <p className="text-lg text-muted-foreground mb-10 leading-relaxed">
            When you're ready, tap Begin. You'll hear a short introduction, then we'll start.
          </p>
          <Button
            size="lg"
            onClick={onBegin}
            className="text-2xl px-12 py-8 h-auto rounded-2xl"
            autoFocus
          >
            Begin
          </Button>
        </div>
      );

    case "playing":
      return (
        <div className="text-center" role="status" aria-live="polite">
          <div className="mx-auto mb-6 flex items-center justify-center">
            <span className="relative flex h-16 w-16">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/40" />
              <span className="relative inline-flex h-16 w-16 rounded-full bg-primary/70" />
            </span>
          </div>
          <p className="text-lg text-foreground">Listening — please follow along.</p>
        </div>
      );

    case "written_consent":
      return renderWrittenConsent();

    case "publicity_release":
      return renderPublicityRelease();

    case "gate_geo":
      return renderGeoGate();

    case "gate_consent":
      return renderGate("consent");
    case "gate_state":
      return renderGate("state");
    case "gate_dob":
      return renderGate("dob");

    case "gates_complete":
    case "assembling":
      return (
        <div className="text-center" role="status" aria-live="polite">
          <Loader2 className="w-10 h-10 text-primary mx-auto mb-4 animate-spin" />
          <p className="text-lg text-muted-foreground">Saving your consent…</p>
        </div>
      );

    case "photo_handoff":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Time to capture the likeness
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            Hand your phone to your helper to take the photos. When they're done,
            you'll come back here to finish the questions.
          </p>
          <Button
            size="lg"
            onClick={onGoToPhotoUpload}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
            autoFocus
          >
            Start photo capture
          </Button>
        </div>
      );

    case "post_photo_handoff":
      // The button MUST be a real user gesture: onBegin calls primeMediaOnGesture,
      // which unlocks the AudioContext the question flow requires (prompt playback +
      // answer-recording analyser). onBegin also routes via stageAfterConsentFinal
      // into consent_assembled — reused directly, priming is NOT duplicated here.
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Photos uploaded
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            We're building the likeness in the background — that usually takes about
            an hour. Next, record the interview so the tribute has their voice and
            story.
          </p>
          <Button
            size="lg"
            onClick={onBegin}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
            autoFocus
          >
            Begin interview recording
          </Button>
          <p className="mt-8 text-sm text-muted-foreground">
            Prefer to come back later? You can return from the original link in your
            email. It stays valid for up to 7 days, unless the interview has already
            been completed.
          </p>
        </div>
      );

    case "consent_assembled":
      return renderConsentAssembled();

    case "questions_complete":
      // Finding #4 BUILD 2: the reconciliation gate owns this render. all_clear /
      // read_failed reproduce the original card + the same final-consent path;
      // still_processing / needs_review / attempts_exhausted add calm notices.
      return renderQuestionsComplete();

    case "final_consent_recording":
      return renderFinalConsent();

    case "final_consent_recorded":
      return (
        <div className="text-center">
          <p className="text-sm uppercase tracking-wide text-muted-foreground mb-3">
            Consent recorded
          </p>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Thank you — you're all finished
          </h1>
          <p className="text-muted-foreground text-lg">
            Your tribute is being prepared. You'll be notified when it's ready to view. You can close this page now.
          </p>
        </div>
      );

    case "exited":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            You can pick this up anytime
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            Your progress is saved. Reopen the link in your email to continue where you left off.
          </p>
          <Button
            size="lg"
            onClick={onRetryResume}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
            autoFocus
          >
            Resume now
          </Button>
        </div>
      );

    case "question_mic_denied":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            We need access to your microphone
          </h1>
          <p className="text-muted-foreground text-lg mb-2">
            Microphone access is needed to continue. Please enable it for this
            page, then reopen the interview link from your email to resume.
          </p>
          <p className="text-sm text-muted-foreground">
            On iPhone or iPad: Settings → Safari → Microphone → Allow.
          </p>
        </div>
      );

    case "assemble_retry":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Let's try that again
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            We hit a snag finishing up — that's on our end, not you. Please tap Try again.
          </p>
          <Button
            size="lg"
            onClick={onRetryAssemble}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
            autoFocus
          >
            Try again
          </Button>
        </div>
      );

    case "resume_retry":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Let's pick up where you left off
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            We couldn't restore your session just now — that's on our end. Please tap Try again.
          </p>
          <Button
            size="lg"
            onClick={onRetryResume}
            className="text-xl px-10 py-7 h-auto rounded-2xl"
            autoFocus
          >
            Try again
          </Button>
        </div>
      );

    case "assemble_failed":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            We couldn't save your responses
          </h1>
          <p className="text-muted-foreground text-lg mb-3">
            Something went wrong on our end saving part of your consent recording,
            and we can't continue automatically. Please contact{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>{" "}
            and we'll help you finish.
          </p>
          {missingGates.length > 0 ? (
            <p className="mt-4 text-xs text-muted-foreground/70 select-all">
              ref: {interviewId ?? "unknown"} · missing {missingGates.join(", ")}
            </p>
          ) : null}
        </div>
      );

    case "session_expired":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Your session has expired
          </h1>
          <p className="text-muted-foreground text-lg">
            Please reopen the link in your email to continue.
          </p>
        </div>
      );

    case "gate_terminated": {
      const reason = terminateInfo?.reason;
      const isDecline = reason === "declined_at_consent_gate";
      const title = isDecline
        ? "Thank you for letting us know"
        : "We're not able to continue";
      // spoken_state_excluded shares the geography copy: to the subject it is the
      // same outcome (their state is excluded), and the distinction between which
      // gate caught it is an internal/audit one. Both must be matched EXPLICITLY —
      // the final branch is the AGE copy, so any unmatched reason silently tells an
      // excluded-state subject they are under 18.
      const body = isDecline
        ? "We won't create an AI version of your voice. You can close this page."
        : reason === "ineligible_geography" || reason === "spoken_state_excluded"
        ? "We're unable to offer voice cloning in some U.S. states at this time."
        : "Voice cloning requires participants to be 18 or older.";
      const recovery = isDecline ? (
        <p className="text-sm text-muted-foreground">
          If you didn't mean to decline, please email{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
            {SUPPORT_EMAIL}
          </a>{" "}
          and we'll help you start fresh.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground">
          If you have questions, please contact{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
      );
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            {title}
          </h1>
          <p className="text-muted-foreground text-lg mb-3">{body}</p>
          {recovery}
        </div>
      );
    }

    case "gate_max_retries":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Let's try that again
          </h1>
          <p className="text-muted-foreground text-lg mb-8">
            We couldn't quite make out your answer. That's on us — let's give it another try, or reach out and we'll help you through it.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => maxRetriesGate && onRetryGate(maxRetriesGate)}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
              autoFocus
            >
              Try this question again
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-base underline underline-offset-4 text-muted-foreground hover:text-foreground"
            >
              Contact us for help
            </a>
          </div>
        </div>
      );

    case "mic_denied":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            We need access to your microphone
          </h1>
          <p className="text-muted-foreground text-lg mb-2">
            Your browser blocked microphone access. Please enable it for this page, then tap Try again below.
          </p>
          <p className="text-sm text-muted-foreground mb-8">
            On iPhone or iPad: Settings → Safari → Microphone → Allow.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Button
              size="lg"
              onClick={() => micDeniedGate && onRetryGate(micDeniedGate)}
              className="text-xl px-10 py-7 h-auto rounded-2xl"
              autoFocus
            >
              Try again
            </Button>
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-base underline underline-offset-4 text-muted-foreground hover:text-foreground"
            >
              Contact us for help
            </a>
          </div>
        </div>
      );

    case "interview_error":
      return (
        <ErrorCard
          title="Something went wrong"
          body={errorMessage ?? "Please refresh the page and try again."}
        />
      );

    case "token_invalid":
      return (
        <ErrorCard
          title="This link isn't valid"
          body="Please open the most recent invitation email and tap the link there."
        />
      );

    case "token_expired":
      return (
        <ErrorCard
          title="This link has expired"
          body="Please contact us and we'll send you a new one."
        />
      );

    case "interview_complete":
      return (
        <ErrorCard
          title="You've already completed this interview"
          body="Thank you. There's nothing more to do here."
        />
      );

    case "interview_terminated":
      return (
        <ErrorCard
          title="This interview can't be resumed"
          body="Please contact LegacyLens if you believe this is a mistake."
        />
      );

    case "missing_token":
      return (
        <div className="text-center">
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
            Open your interview link
          </h1>
          <p className="text-muted-foreground text-lg mb-3">
            To continue, open the original interview link from your LegacyLens
            email. It stays valid for up to 7 days, unless the interview has
            already been completed.
          </p>
          <p className="text-sm text-muted-foreground">
            Can't find the email? Contact us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </div>
      );

    case "generic_error":
    default:
      return (
        <ErrorCard
          title="Something went wrong"
          body="Please refresh the page or try again in a few minutes."
        />
      );
  }
};

const ErrorCard = ({ title, body }: { title: string; body: string }) => (
  <div className="text-center">
    <h1 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">{title}</h1>
    <p className="text-muted-foreground text-lg">{body}</p>
  </div>
);

export default InterviewLanding;
